import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Investment {
  id: string;
  user_id: string;
  name: string;
  category: 'Machines atelier' | 'Outillages' | 'Informatique' | 'Mobilier scolaire' | 'Travaux bâtiment' | 'Autre';
  description?: string;
  total_budget: number;
  planned_year?: string;
  planned_start_date?: string;
  priority: 'Must' | 'Should' | 'Could';
  status: 'Planned' | 'In progress' | 'Done' | 'Cancelled';
  supplier?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface InvestmentDisbursement {
  id: string;
  user_id: string;
  investment_id: string;
  label: string;
  amount: number;
  due_date: string;
  is_locked: boolean;
  source: 'Manual' | 'Imported';
  created_at: string;
  updated_at: string;
}

export interface InvestmentWithStats extends Investment {
  disbursements_total: number;
  disbursements_count: number;
  funded_amount: number;
  remaining_to_fund: number;
}

export function useInvestments() {
  const [investments, setInvestments] = useState<InvestmentWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { effectiveUserId } = useAuth();

  const loadInvestments = async () => {
    if (!effectiveUserId) return;

    try {
      setLoading(true);
      setError(null);

      const { data: investmentsData, error: investmentsError } = await supabase
        .from('investments')
        .select('*')
        .eq('user_id', effectiveUserId)
        .order('created_at', { ascending: false });

      if (investmentsError) throw investmentsError;

      const investmentIds = investmentsData?.map(i => i.id) || [];

      const [disbursementsData, allocationsData] = await Promise.all([
        investmentIds.length > 0
          ? supabase
              .from('investment_disbursements')
              .select('investment_id, amount')
              .in('investment_id', investmentIds)
          : Promise.resolve({ data: [], error: null }),
        investmentIds.length > 0
          ? supabase
              .from('funding_allocations')
              .select('investment_id, allocated_amount')
              .in('investment_id', investmentIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      const disbursementsByInv = (disbursementsData.data || []).reduce((acc, d) => {
        if (!acc[d.investment_id]) {
          acc[d.investment_id] = { total: 0, count: 0 };
        }
        acc[d.investment_id].total += parseFloat(d.amount);
        acc[d.investment_id].count += 1;
        return acc;
      }, {} as Record<string, { total: number; count: number }>);

      const fundingByInv = (allocationsData.data || []).reduce((acc, a) => {
        if (!acc[a.investment_id]) {
          acc[a.investment_id] = 0;
        }
        acc[a.investment_id] += parseFloat(a.allocated_amount);
        return acc;
      }, {} as Record<string, number>);

      const enriched: InvestmentWithStats[] = (investmentsData || []).map(inv => {
        const disbStats = disbursementsByInv[inv.id] || { total: 0, count: 0 };
        const funded = fundingByInv[inv.id] || 0;
        return {
          ...inv,
          disbursements_total: disbStats.total,
          disbursements_count: disbStats.count,
          funded_amount: funded,
          remaining_to_fund: parseFloat(inv.total_budget) - funded
        };
      });

      setInvestments(enriched);
    } catch (err: any) {
      console.error('Error loading investments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvestments();
  }, [effectiveUserId]);

  const createInvestment = async (investment: Omit<Investment, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!effectiveUserId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('investments')
      .insert([{ ...investment, user_id: effectiveUserId }])
      .select()
      .single();

    if (error) throw error;
    await loadInvestments();
    return data;
  };

  const updateInvestment = async (id: string, updates: Partial<Investment>) => {
    const { error } = await supabase
      .from('investments')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (error) throw error;
    await loadInvestments();
  };

  const deleteInvestment = async (id: string) => {
    const { error } = await supabase
      .from('investments')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (error) throw error;
    await loadInvestments();
  };

  return {
    investments,
    loading,
    error,
    refresh: loadInvestments,
    createInvestment,
    updateInvestment,
    deleteInvestment
  };
}

export function useInvestmentDetail(investmentId: string) {
  const [investment, setInvestment] = useState<Investment | null>(null);
  const [disbursements, setDisbursements] = useState<InvestmentDisbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { effectiveUserId } = useAuth();

  const loadInvestmentDetail = async () => {
    if (!effectiveUserId || !investmentId) return;

    try {
      setLoading(true);
      setError(null);

      const [invResult, disbResult] = await Promise.all([
        supabase
          .from('investments')
          .select('*')
          .eq('id', investmentId)
          .eq('user_id', effectiveUserId)
          .maybeSingle(),
        supabase
          .from('investment_disbursements')
          .select('*')
          .eq('investment_id', investmentId)
          .eq('user_id', effectiveUserId)
          .order('due_date', { ascending: true })
      ]);

      if (invResult.error) throw invResult.error;
      if (disbResult.error) throw disbResult.error;

      setInvestment(invResult.data);
      setDisbursements(disbResult.data || []);
    } catch (err: any) {
      console.error('Error loading investment detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvestmentDetail();
  }, [effectiveUserId, investmentId]);

  const createDisbursement = async (disbursement: Omit<InvestmentDisbursement, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!effectiveUserId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('investment_disbursements')
      .insert([{ ...disbursement, user_id: effectiveUserId }])
      .select()
      .single();

    if (error) throw error;
    await loadInvestmentDetail();
    return data;
  };

  const updateDisbursement = async (id: string, updates: Partial<InvestmentDisbursement>) => {
    const { error } = await supabase
      .from('investment_disbursements')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (error) throw error;
    await loadInvestmentDetail();
  };

  const deleteDisbursement = async (id: string) => {
    const { error } = await supabase
      .from('investment_disbursements')
      .delete()
      .eq('id', id)
      .eq('user_id', effectiveUserId);

    if (error) throw error;
    await loadInvestmentDetail();
  };

  return {
    investment,
    disbursements,
    loading,
    error,
    refresh: loadInvestmentDetail,
    createDisbursement,
    updateDisbursement,
    deleteDisbursement
  };
}
